import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Alert, Table, Breadcrumbs, Anchor, Badge, Stack, List } from '@mantine/core';
import { Lightbulb, Warning, Check, X, MagnifyingGlass } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function KeywordMatchTypesGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Typy dopasowania słów kluczowych w Google Ads"
        description="Przybliżone, fraza, dokładne. Poznaj różnice między typami dopasowania słów kluczowych w Google Ads i dowiedz się, kiedy używać każdego z nich."
        path={`${guidesBase}/keyword-match-types`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Typy dopasowania słów kluczowych</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Typy dopasowania słów kluczowych</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Typ dopasowania decyduje o tym, na jakie zapytania użytkowników wyświetli się Twoja reklama. Wybór odpowiedniego typu to balans między zasięgiem a precyzją.</Text>
      </Alert>

      {/* Overview */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Trzy typy dopasowania</Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="md">
          Google Ads oferuje trzy typy dopasowania słów kluczowych. Każdy typ określa, jak ściśle zapytanie użytkownika musi odpowiadać Twojemu słowu kluczowemu, aby reklama mogła się wyświetlić.
        </Text>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Typ</Table.Th>
              <Table.Th>Zapis</Table.Th>
              <Table.Th>Zasięg</Table.Th>
              <Table.Th>Precyzja</Table.Th>
              <Table.Th>Kiedy używać</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td><Badge color="violet" size="sm" variant="light">Przybliżone</Badge></Table.Td>
              <Table.Td><Text size="sm" ff="monospace">buty sportowe</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light">Najszerszy</Badge></Table.Td>
              <Table.Td><Badge color="red" size="xs" variant="light">Najniższa</Badge></Table.Td>
              <Table.Td><Text size="xs" c="dimmed">Nowe kampanie, odkrywanie fraz</Text></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Badge color="blue" size="sm" variant="light">Fraza</Badge></Table.Td>
              <Table.Td><Text size="sm" ff="monospace">"buty sportowe"</Text></Table.Td>
              <Table.Td><Badge color="yellow" size="xs" variant="light">Średni</Badge></Table.Td>
              <Table.Td><Badge color="yellow" size="xs" variant="light">Średnia</Badge></Table.Td>
              <Table.Td><Text size="xs" c="dimmed">Większość kampanii, dobry balans</Text></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Badge color="indigo" size="sm" variant="light">Dokładne</Badge></Table.Td>
              <Table.Td><Text size="sm" ff="monospace">[buty sportowe]</Text></Table.Td>
              <Table.Td><Badge color="red" size="xs" variant="light">Najwęższy</Badge></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light">Najwyższa</Badge></Table.Td>
              <Table.Td><Text size="xs" c="dimmed">Sprawdzone frazy z konwersjami</Text></Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Broad Match */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="xs">
          <Badge color="violet" size="sm" variant="light" mr="xs">Przybliżone</Badge>
          Dopasowanie przybliżone (Broad Match)
        </Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="md">
          Reklama wyświetla się na zapytania powiązane z Twoim słowem kluczowym, w tym synonimy, powiązane wyszukiwania, odmiany i luźne skojarzenia. Google sam decyduje, co jest "powiązane".
        </Text>

        <Text size="xs" fw={500} mb="xs">Słowo kluczowe: <Text span ff="monospace" c="dark">buty sportowe</Text></Text>
        <Table verticalSpacing="xs" horizontalSpacing="md" mb="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Zapytanie użytkownika</Table.Th>
              <Table.Th>Wyświetli się?</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td><Text size="sm">buty sportowe damskie</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">obuwie do biegania</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">adidasy na siłownię</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">buty trekkingowe męskie</Text></Table.Td>
              <Table.Td><Badge color="yellow" size="xs" variant="light">Możliwe</Badge></Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        <Alert color="yellow" icon={<Warning size={14} />}>
          <Text size="xs">Dopasowanie przybliżone daje najwięcej wyświetleń, ale może generować kliknięcia na nieistotne zapytania. Zawsze łącz z wykluczającymi słowami kluczowymi i regularnie sprawdzaj raport wyszukiwanych haseł.</Text>
        </Alert>
      </Paper>

      {/* Phrase Match */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="xs">
          <Badge color="blue" size="sm" variant="light" mr="xs">Fraza</Badge>
          Dopasowanie do frazy (Phrase Match)
        </Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="md">
          Reklama wyświetla się, gdy zapytanie zawiera znaczenie Twojego słowa kluczowego. Zapytanie może zawierać dodatkowe słowa przed lub po, ale sens frazy musi być zachowany.
        </Text>

        <Text size="xs" fw={500} mb="xs">Słowo kluczowe: <Text span ff="monospace" c="dark">"buty sportowe"</Text></Text>
        <Table verticalSpacing="xs" horizontalSpacing="md" mb="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Zapytanie użytkownika</Table.Th>
              <Table.Th>Wyświetli się?</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td><Text size="sm">buty sportowe damskie</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">tanie buty sportowe dla dzieci</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">buty do biegania sportowe</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">obuwie do biegania</Text></Table.Td>
              <Table.Td><Badge color="red" size="xs" variant="light" leftSection={<X size={10} />}>Nie</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">adidasy na siłownię</Text></Table.Td>
              <Table.Td><Badge color="red" size="xs" variant="light" leftSection={<X size={10} />}>Nie</Badge></Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        <Alert color="blue" icon={<Lightbulb size={14} />}>
          <Text size="xs">Dopasowanie do frazy to najczęściej rekomendowany typ. Daje dobry zasięg przy zachowaniu kontroli nad intencją użytkownika. Droid domyślnie dodaje słowa kluczowe w dopasowaniu do frazy.</Text>
        </Alert>
      </Paper>

      {/* Exact Match */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="xs">
          <Badge color="indigo" size="sm" variant="light" mr="xs">Dokładne</Badge>
          Dopasowanie dokładne (Exact Match)
        </Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="md">
          Reklama wyświetla się tylko wtedy, gdy zapytanie ma dokładnie to samo znaczenie co Twoje słowo kluczowe. Google dopuszcza drobne warianty: literówki, formę liczby mnogiej i synonimy bliskie znaczeniowo.
        </Text>

        <Text size="xs" fw={500} mb="xs">Słowo kluczowe: <Text span ff="monospace" c="dark">[buty sportowe]</Text></Text>
        <Table verticalSpacing="xs" horizontalSpacing="md" mb="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Zapytanie użytkownika</Table.Th>
              <Table.Th>Wyświetli się?</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td><Text size="sm">buty sportowe</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">sportowe buty</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">but sportowy</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light" leftSection={<Check size={10} />}>Tak (odmiana)</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">buty sportowe damskie</Text></Table.Td>
              <Table.Td><Badge color="red" size="xs" variant="light" leftSection={<X size={10} />}>Nie</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">tanie buty sportowe</Text></Table.Td>
              <Table.Td><Badge color="red" size="xs" variant="light" leftSection={<X size={10} />}>Nie</Badge></Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        <Alert color="indigo" icon={<Lightbulb size={14} />}>
          <Text size="xs">Dopasowanie dokładne daje najwyższy CTR i najlepszą konwersję, ale najmniejszy zasięg. Używaj do fraz, które już sprawdziłeś i wiesz, że generują konwersje.</Text>
        </Alert>
      </Paper>

      {/* Strategy */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Strategia: od szerokiego do wąskiego</Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="md">
          Najskuteczniejsza strategia to stopniowe zawężanie dopasowania w miarę zbierania danych.
        </Text>
        <Stack gap="sm">
          <Paper p="sm" bg="violet.0" radius="md">
            <Text size="xs" fw={600} c="violet.8" mb={4}>1. Start: Przybliżone lub Fraza</Text>
            <Text size="xs" c="dimmed" lh={1.5}>Zacznij od dopasowania do frazy (lub przybliżonego z niskim budżetem). Zbieraj dane o tym, jakie zapytania generują kliknięcia i konwersje.</Text>
          </Paper>
          <Paper p="sm" bg="blue.0" radius="md">
            <Text size="xs" fw={600} c="blue.8" mb={4}>2. Analiza: Raport wyszukiwanych haseł</Text>
            <Text size="xs" c="dimmed" lh={1.5}>Po 2-4 tygodniach sprawdź raport search terms. Znajdź frazy, które konwertują, i te, które marnują budżet.</Text>
          </Paper>
          <Paper p="sm" bg="indigo.0" radius="md">
            <Text size="xs" fw={600} c="indigo.8" mb={4}>3. Optymalizacja: Dodaj konwertujące frazy jako Dokładne</Text>
            <Text size="xs" c="dimmed" lh={1.5}>Frazy z najlepszymi wynikami dodaj jako osobne słowa kluczowe w dopasowaniu dokładnym z wyższą stawką CPC. Nieistotne frazy wyklucz.</Text>
          </Paper>
          <Paper p="sm" bg="gray.0" radius="md">
            <Text size="xs" fw={600} c="gray.8" mb={4}>4. Skalowanie: Mieszaj typy dopasowania</Text>
            <Text size="xs" c="dimmed" lh={1.5}>Docelowo używaj mieszanki: Fraza do odkrywania nowych zapytań, Dokładne do sprawdzonych fraz z wysoką konwersją. Przybliżone tylko przy Smart Bidding.</Text>
          </Paper>
        </Stack>
      </Paper>

      {/* Droid + match types */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Jak Droid pracuje z typami dopasowania</Text>
        <List size="sm" spacing="xs" c="dimmed">
          <List.Item>Nowe słowa kluczowe dodawane są domyślnie w dopasowaniu do frazy (najlepszy balans)</List.Item>
          <List.Item>Agent automatycznie analizuje raport wyszukiwanych haseł i dodaje wykluczenia</List.Item>
          <List.Item>Frazy z konwersjami mogą być zasugerowane do dodania w dopasowaniu dokładnym</List.Item>
          <List.Item>W tabeli słów kluczowych typ dopasowania jest widoczny pod nazwą każdego słowa</List.Item>
          <List.Item>Możesz ręcznie zmienić typ dopasowania przy dodawaniu nowego słowa kluczowego</List.Item>
        </List>
      </Paper>

      {/* Common mistakes */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Częste błędy</Text>
        <Stack gap="xs">
          <Alert color="red" variant="light" icon={<Warning size={14} />}>
            <Text size="xs"><Text span fw={600}>Tylko dopasowanie przybliżone bez wykluczeń.</Text> Generuje dużo nieistotnych kliknięć. Zawsze łącz z listą wykluczających słów.</Text>
          </Alert>
          <Alert color="red" variant="light" icon={<Warning size={14} />}>
            <Text size="xs"><Text span fw={600}>Tylko dopasowanie dokładne od startu.</Text> Ogranicza zasięg i uniemożliwia odkrycie wartościowych fraz, o których nie pomyślałeś.</Text>
          </Alert>
          <Alert color="red" variant="light" icon={<Warning size={14} />}>
            <Text size="xs"><Text span fw={600}>Te same słowa we wszystkich typach dopasowania.</Text> Powoduje kanibalizację, gdzie Twoje słowa kluczowe konkurują ze sobą o te same zapytania.</Text>
          </Alert>
          <Alert color="green" variant="light" icon={<Check size={14} />}>
            <Text size="xs"><Text span fw={600}>Dobra praktyka:</Text> Używaj frazy jako domyślnego typu. Dodawaj dokładne dopasowanie tylko dla sprawdzonych fraz z konwersjami. Przybliżone stosuj z ostrożnością i tylko ze Smart Bidding.</Text>
          </Alert>
        </Stack>
      </Paper>
    </Box>
  );
}
